import { getInitData } from './telegram';

/**
 * API client.
 *
 * Sessions are cookie-based, so every request sends credentials and echoes the
 * CSRF token. The client never computes a price or trusts a local total — it
 * asks the server and renders what comes back.
 */

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:4000';

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let csrfToken: string | null = null;

/*
 * Session token, in memory only.
 *
 * The API also sets an httpOnly cookie, which is the better carrier and is what
 * gets used same-origin. But the Mini App is served from one host and calls the
 * API on another, and iOS refuses that cookie — login succeeded and every
 * request after it came back 401. This is the fallback for those installs.
 *
 * Deliberately not in localStorage: the app re-authenticates from `initData` on
 * every load anyway, so persisting it would add exposure and buy nothing.
 */
let sessionToken: string | null = null;

const readCsrfCookie = (): string | null => {
  const match = document.cookie.match(/(?:^|;\s*)store_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]!) : null;
};

/**
 * Reads the server's envelope, or throws the error inside it.
 *
 * Shared by the customer's calls and the management ones so both fail with the
 * same `ApiError` and the same Russian message — a screen never has to know
 * which half of the client it is talking to in order to render a failure.
 */
async function readEnvelope<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = (payload as { error?: { code: string; message: string; details?: unknown } })
      .error;
    throw new ApiError(
      error?.code ?? 'internal_error',
      error?.message ?? 'Не удалось выполнить запрос',
      response.status,
      error?.details,
    );
  }

  return payload as T;
}

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { accept: 'application/json' };

  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (sessionToken) headers.authorization = `Bearer ${sessionToken}`;

  if (method !== 'GET') {
    const token = csrfToken ?? readCsrfCookie();
    if (token) headers['x-csrf-token'] = token;
  }

  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    credentials: 'include',
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  return readEnvelope<T>(response);
}

export interface MoneyJson {
  amount: string;
  currency: string;
  decimals: number;
}

/**
 * The one channel this client speaks for. Sent on every checkout call because
 * eligibility, provider configuration and the terms notice are all decided per
 * channel server-side.
 */
const CHANNEL = 'telegram_mini_app' as const;

export interface SessionResponse {
  user: {
    id: string;
    telegramId: string;
    firstName: string | null;
    username: string | null;
    locale: string;
    referralCode: string;
  };
  roles: string[];
  permissions: string[];
  csrfToken: string;
  sessionToken: string;
}

/**
 * A category exactly as `/api/catalog/categories` returns it.
 *
 * The client used to declare three of these eight fields, and `imageUrl` was
 * one of the five it dropped — so a tile could never show the brand mark the
 * owner had already saved on the row. Nothing about the server changed here;
 * the type was the whole obstacle.
 */
export interface CategorySummary {
  id: string;
  shortId: string;
  parentId: string | null;
  slug: string;
  title: string;
  description: string | null;
  iconEmoji: string | null;
  /** A URL the owner pasted. Image storage is unconfigured, so expect `null`
      often and fall back to the emoji or a monogram. */
  imageUrl: string | null;
}

export interface ProductSummary {
  id: string;
  shortId: string;
  title: string;
  subtitle: string | null;
  categoryId: string | null;
  price: MoneyJson;
  listPrice: MoneyJson | null;
  isFeatured: boolean;
  available: number | null;
}

export interface VariantDetail {
  id: string;
  title: string;
  durationDays: number | null;
  price: MoneyJson;
  listPrice: MoneyJson | null;
  isDefault: boolean;
  available: number | null;
}

export interface ProductDetail {
  product: ProductSummary & {
    description: string | null;
    activationMethod: string | null;
    activationInstructions: string | null;
    refundPolicy: string | null;
    fulfillmentMode: string;
  };
  variants: VariantDetail[];
  media: Array<{ url: string; kind: string; alt: string | null }>;
  related: Array<{ id: string; shortId: string; title: string; price: MoneyJson }>;
}

export interface OrderSummary {
  id: string;
  orderNumber: string;
  status: string;
  statusLabel: string;
  totalAmount: MoneyJson;
  createdAt: string;
  deliveredAt: string | null;
  title: string | null;
  variantTitle: string | null;
}

/*
 * Payment vocabulary.
 *
 * These mirror `PAYMENT_PROVIDERS` and `PAYMENT_METHODS` in `@store/shared`,
 * and the zod enums `/api/checkout/quote` and `/api/checkout/payment` parse
 * with. Restated here rather than imported for the same reason `MoneyJson` is:
 * this module is the client's copy of the wire format, and it should fail to
 * compile if the wire format moves, not silently follow a server refactor.
 */
/*
 * The server's own lists, restated because this bundle does not import the
 * domain package. They have to be kept level with `@store/shared`'s
 * `PAYMENT_PROVIDERS` and `PAYMENT_METHODS`: a method the server offers and this
 * union omits is one the screen cannot render a branch for, which is how `ton`
 * would have arrived as «Другой способ» with a card glyph.
 */
export type PaymentProviderId = 'telegram_stars' | 'crypto_pay' | 'ton' | 'mock';
export type PaymentMethod = 'telegram_stars' | 'crypto' | 'ton' | 'mock';

/**
 * How the client is meant to open `paymentUrl`.
 *
 * Three different openers, and Telegram rejects the wrong one silently:
 * `telegram_invoice` is a `t.me/$slug` for `openInvoice()`, `telegram_link` is
 * any other `t.me/…` and needs `openTelegramLink()`, and `external_link` leaves
 * Telegram. Collapsing them into one nullable string is what made Crypto Pay
 * look broken — its URL is a `telegram_link`, and `openInvoice()` refuses it.
 */
export type PaymentUrlKind = 'telegram_invoice' | 'telegram_link' | 'external_link';

/**
 * One chosen way to pay, carried from `/api/checkout/methods` through the
 * quote to the invoice. Build it with {@link toPaymentSelection} from an
 * option the server offered; do not assemble one by hand, or the client is
 * back to deciding what it is allowed to charge.
 */
export interface PaymentSelection {
  providerId: PaymentProviderId;
  method: PaymentMethod;
  /** Currency the invoice is denominated in: `XTR` for Stars, the store's
      configured fiat or asset code for Crypto Pay. */
  paymentCurrency: string;
  /** Crypto Pay only, and only when the store lets the customer choose the
      asset. Ignored by `/quote`; `/payment` reads it. */
  asset?: string;
}

/** Telegram Stars, the method every store has. The default so a caller that
    does not offer a choice yet still names what it is asking for. */
export const STARS_PAYMENT: PaymentSelection = {
  providerId: 'telegram_stars',
  method: 'telegram_stars',
  paymentCurrency: 'XTR',
};

export interface PaymentMethodOption {
  providerId: PaymentProviderId;
  method: PaymentMethod;
  currency: string;
}

/** Turns an offered method into a selection. The rename of `currency` to
    `paymentCurrency` is the whole reason this exists — getting it wrong quotes
    in the wrong currency. */
export const toPaymentSelection = (
  option: PaymentMethodOption,
  asset?: string,
): PaymentSelection => ({
  providerId: option.providerId,
  method: option.method,
  paymentCurrency: option.currency,
  ...(asset ? { asset } : {}),
});

export type PricingStepName =
  | 'base_price'
  | 'variant_price'
  | 'price_override'
  | 'catalog_promotion'
  | 'promo_code'
  | 'bonus'
  | 'payment_adjustment'
  | 'exchange_rate'
  | 'provider_markup'
  | 'fixed_fee'
  | 'rounding'
  | 'minimum_amount';

export interface PricingStep {
  order: number;
  step: PricingStepName;
  /** Already in the customer's language; render it, do not translate it. */
  description: string;
  before: MoneyJson;
  after: MoneyJson;
  delta: MoneyJson;
  meta?: Record<string, unknown>;
}

/**
 * The server's price breakdown, in full.
 *
 * Two currencies live in here at once: everything down to `subtotal` is in the
 * store's base currency and everything from the exchange step on is in the
 * payment currency. Every amount carries its own, so pass each through
 * `formatMoney` and never add two of them together.
 */
export interface QuoteBreakdown {
  version: string;
  baseCurrency: string;
  paymentCurrency: string;
  quantity: number;
  /** Undiscounted, for the struck-through "old price". */
  listAmount: MoneyJson;
  catalogDiscount: MoneyJson;
  promoDiscount: MoneyJson;
  /** Balance actually applied — the requested amount capped by the policy and
      by what the account holds. Base currency. */
  bonusApplied: MoneyJson;
  paymentAdjustment: MoneyJson;
  /** Base-currency amount owed before conversion. */
  subtotal: MoneyJson;
  exchangeRate: string | null;
  exchangeRateSource: string | null;
  exchangeRuleId: string | null;
  providerMarkup: MoneyJson;
  fixedFee: MoneyJson;
  roundingDelta: MoneyJson;
  minimumAdjustment: MoneyJson;
  /** What the customer actually pays, in the payment currency. Same value as
      `quote.finalAmount`. */
  finalAmount: MoneyJson;
  steps: PricingStep[];
}

export interface QuoteRequest {
  productId: string;
  variantId: string | null;
  promoCode?: string | null;
  /**
   * Balance to spend, in minor units of the base currency, as a digit string —
   * `'15000'` is 150 ₽. The server caps it by policy and by the account, and
   * reports what it actually took as `breakdown.bonusApplied`; the client never
   * decides how much a balance covers.
   */
  bonusAmount?: string | null;
  /** Omitted means Stars. Pass a selection to quote for anything else. */
  payment?: PaymentSelection;
}

export interface QuoteResponse {
  quote: {
    id: string;
    productId: string;
    variantId: string | null;
    quantity: number;
    paymentCurrency: string;
    finalAmount: MoneyJson;
    expiresAt: string;
    breakdown: QuoteBreakdown;
  };
  availableStock: number | null;
  promoRejection: { key: string; message: string } | null;
  notices: { stars: string; rate: string; terms: string };
}

export interface OrderCreated {
  order: {
    id: string;
    orderNumber: string;
    status: string;
    totalAmount: MoneyJson;
    expiresAt: string | null;
  };
}

export interface PaymentCreated {
  payment: {
    id: string;
    status: string;
    amount: MoneyJson;
    expiresAt: string | null;
  };
  /** Echoed back so the client opens the URL the way the provider that issued
      it expects, rather than the way the client asked for. */
  providerId: PaymentProviderId;
  method: PaymentMethod;
  paymentUrl: string | null;
  /** `null` exactly when there is no URL to open. */
  paymentUrlKind: PaymentUrlKind | null;
}

export interface BalanceResponse {
  balance: MoneyJson;
  reserved: MoneyJson;
  available: MoneyJson;
  entries: Array<{
    id: string;
    type: string;
    amount: MoneyJson;
    reason: string;
    orderId: string | null;
    createdAt: string;
    expiresAt: string | null;
  }>;
  disclaimer: string;
}

export interface ReferralsResponse {
  code: string;
  link: string;
  invited: number;
  /**
   * Grouped by currency. A store paying rewards in more than one currency has
   * no single meaningful total, so the server never invents one.
   */
  earned: MoneyJson[];
  grantedRewards: number;
  pendingRewards: number;
  howItWorks: string;
}

export interface ProfileStats {
  delivered: number;
  totalOrders: number;
  /** Grouped by the order's base currency, for the same reason as `earned`. */
  spent: MoneyJson[];
  memberSince: string | null;
}

export interface ProfileResponse {
  stats: ProfileStats;
  balance: BalanceResponse;
  referrals: ReferralsResponse;
}

/**
 * A promo banner for the home screen, assembled server-side from the content
 * system — so the owner edits it where they edit every other piece of copy.
 *
 * Matches `/api/catalog/banners` field for field; the route resolves the
 * destination server-side, so nothing here has to look anything up.
 */
export interface HomeBanner {
  /** Slot identity, stable across edits — the content key behind it. */
  id: string;
  title: string;
  subtitle: string | null;
  /** Pasted URL. Null is the normal case while storage is unconfigured. */
  imageUrl: string | null;
  /** Shown when there is no image. */
  emoji: string | null;
  /** Where a tap goes. `null` means the banner is not tappable. */
  link: BannerLink | null;
}

export type BannerLink =
  | { kind: 'product'; value: string }
  | { kind: 'category'; value: string }
  | { kind: 'url'; value: string };

/**
 * Storefront-wide proof, for the home screen.
 *
 * `delivered` is `null`, not `0`, when the store has not passed the threshold
 * the server applies — "0 продано" is worse than no number at all, and the
 * decision is the server's so every surface tells the same story.
 */
export interface StorefrontStats {
  delivered: number | null;
}

export const api = {
  authenticate: async (): Promise<SessionResponse> => {
    const session = await request<SessionResponse>('/api/auth/telegram', {
      method: 'POST',
      body: { initData: getInitData(), scope: 'miniapp' },
    });
    csrfToken = session.csrfToken;
    sessionToken = session.sessionToken;
    return session;
  },

  categories: () => request<{ categories: CategorySummary[] }>('/api/catalog/categories'),

  products: (params: { categoryId?: string; search?: string; featured?: boolean } = {}) => {
    const query = new URLSearchParams();
    if (params.categoryId) query.set('categoryId', params.categoryId);
    if (params.search) query.set('search', params.search);
    if (params.featured) query.set('featured', 'true');
    return request<{ products: ProductSummary[] }>(`/api/catalog/products?${query.toString()}`);
  },

  product: (idOrShortId: string) =>
    request<ProductDetail>(`/api/catalog/products/${encodeURIComponent(idOrShortId)}`),

  /** Home banners, edited through the content system. Public: no session. */
  banners: () => request<{ banners: HomeBanner[] }>('/api/catalog/banners'),

  /** Public counter for the home screen, already thresholded server-side. */
  storefrontStats: () => request<StorefrontStats>('/api/catalog/storefront-stats'),

  /**
   * Asks the server what this basket costs, paid this way.
   *
   * The provider used to be written into the body here, so `/checkout/methods`
   * was a list the customer could look at and not a choice they could make —
   * every quote was Stars in XTR no matter what was on screen. It is an
   * argument now: the same call quotes Crypto Pay in roubles.
   */
  quote: ({ payment = STARS_PAYMENT, ...body }: QuoteRequest) =>
    request<QuoteResponse>('/api/checkout/quote', {
      method: 'POST',
      body: {
        productId: body.productId,
        variantId: body.variantId,
        promoCode: body.promoCode ?? null,
        bonusAmount: body.bonusAmount ?? null,
        channel: CHANNEL,
        providerId: payment.providerId,
        method: payment.method,
        paymentCurrency: payment.paymentCurrency,
      },
    }),

  createOrder: (quoteId: string) =>
    request<OrderCreated>('/api/checkout/order', {
      method: 'POST',
      body: { quoteId, channel: CHANNEL, acceptTerms: true },
    }),

  /** Pass the same selection the quote was priced with. Quoting for one
      provider and invoicing with another is a different price. */
  createPayment: (orderId: string, payment: PaymentSelection = STARS_PAYMENT) =>
    request<PaymentCreated>('/api/checkout/payment', {
      method: 'POST',
      body: {
        orderId,
        channel: CHANNEL,
        providerId: payment.providerId,
        method: payment.method,
        ...(payment.asset ? { asset: payment.asset } : {}),
      },
    }),

  /**
   * Releases an order the customer is not going to pay for.
   *
   * An order holds allocated stock and any balance it spent. Abandoning the
   * invoice used to leave both held until the reaper ran, so the customer's
   * own key sat reserved against them and their balance read short.
   */
  cancelOrder: (orderId: string) =>
    request<{ order: { id: string; status: string }; productKind: 'digital' | 'service' }>(
      '/api/checkout/cancel',
      { method: 'POST', body: { orderId } },
    ),

  /**
   * Everything the profile screen needs, in one request. The screen used to
   * open three connections before it could render anything.
   */
  profile: () => request<ProfileResponse>('/api/me/profile'),

  balance: () => request<BalanceResponse>('/api/me/balance'),

  referrals: () => request<ReferralsResponse>('/api/me/referrals'),

  stats: () => request<ProfileStats>('/api/me/stats'),

  /** Which payment methods the server will actually accept in this channel. */
  paymentMethods: () =>
    request<{ methods: PaymentMethodOption[]; notice: string }>(
      `/api/checkout/methods?channel=${CHANNEL}`,
    ),

  orders: (status?: string) =>
    request<{ orders: OrderSummary[] }>(
      `/api/orders${status ? `?status=${encodeURIComponent(status)}` : ''}`,
    ),

  order: (orderId: string) =>
    request<{
      order: OrderSummary & {
        breakdown: unknown;
        paidAt: string | null;
        /** When the payment window closes, ISO. The server cancels a draft or
            awaiting-payment order once it passes, so it is what a countdown
            counts to. `null` means the order has no deadline at all — render
            nothing, not a timer at zero. Detail only: the list has no timer,
            so `/api/orders` does not carry it. */
        expiresAt: string | null;
      };
      items: Array<{
        title: string;
        variantTitle: string | null;
        instructions: string | null;
        lineTotal: MoneyJson;
      }>;
      payment: { status: string; paymentUrl: string | null } | null;
      fulfillment: { status: string } | null;
    }>(`/api/orders/${orderId}`),

  orderContent: (orderId: string) =>
    request<{ content: string; instructions: string | null; notice: string }>(
      `/api/orders/${orderId}/content`,
    ),

  tickets: () =>
    request<{
      tickets: Array<{ id: string; number: number; subject: string; statusLabel: string }>;
      notice: string;
    }>('/api/support/tickets'),

  createTicket: (body: { subject: string; body: string; orderId?: string | null }) =>
    request<{ ticket: { id: string; number: number }; message: string }>('/api/support/tickets', {
      method: 'POST',
      body,
    }),
};

/* =========================================================================
 * Управление магазином — the management surface.
 *
 * Everything below talks to `/api/admin`, which is the same API the web panel
 * drives: one product table, one variant system, one stock system, one
 * publication rule. Nothing here is a second catalogue.
 *
 * It is a separate object from `api` on purpose. A customer's screen imports
 * `api`; only the management section imports `adminApi`, and every call in it
 * refuses locally until {@link adminApi.signIn} has succeeded — so an ordinary
 * customer's session cannot emit an administrative request even by accident.
 * ========================================================================= */

/**
 * The server's permission vocabulary, restated in full.
 *
 * Restated rather than imported for the same reason `MoneyJson` is: this module
 * is the client's copy of the wire format. A partial copy would be the trap —
 * the next screen that needs `orders.fulfill` would either widen this to
 * `string` or invent a literal, and a permission name that is one character off
 * silently never matches. The list mirrors `PERMISSIONS` in
 * `packages/shared/src/auth/permissions.ts`.
 */
export type AdminPermission =
  | 'catalog.read'
  | 'catalog.write'
  | 'catalog.publish'
  | 'inventory.read'
  | 'inventory.write'
  | 'inventory.reveal'
  | 'inventory.export'
  | 'orders.read'
  | 'orders.write'
  | 'orders.refund'
  | 'orders.fulfill'
  | 'users.read'
  | 'users.write'
  | 'users.block'
  | 'bonus.adjust'
  | 'promo.read'
  | 'promo.write'
  | 'payments.read'
  | 'payments.configure'
  | 'rates.read'
  | 'rates.write'
  | 'support.read'
  | 'support.reply'
  | 'support.assign'
  | 'broadcast.read'
  | 'broadcast.write'
  | 'broadcast.send'
  | 'analytics.read'
  | 'audit.read'
  | 'settings.read'
  | 'settings.write'
  | 'admins.manage';

export type AdminRoleName =
  'owner' | 'admin' | 'catalog_manager' | 'support' | 'marketing' | 'analyst' | 'viewer';

/**
 * Whether the caller holds a permission, according to the session the server
 * issued. `permissions` is computed server-side from roles read out of the
 * database; nothing the client can set influences it.
 */
export const hasPermission = (
  session: SessionResponse | null | undefined,
  permission: AdminPermission,
): boolean => Boolean(session?.permissions.includes(permission));

export const hasRole = (
  session: SessionResponse | null | undefined,
  role: AdminRoleName,
): boolean => Boolean(session?.roles.includes(role));

/**
 * What the management section may show this person.
 *
 * Hiding a control is presentation and nothing more — every one of these
 * answers is the server's, and the server refuses again with 403 when the
 * request actually arrives. Both must exist: without the flags an owner sees
 * buttons that always fail, and without the 403 the flags would be the only
 * thing standing between a curious customer and the catalogue.
 */
export interface ManagementAccess {
  /** Show the management group inside Профиль at all. True when there is
      something to *do* there — a viewer with read-only rights gets nothing. */
  readonly canOpen: boolean;
  readonly canReadCatalog: boolean;
  readonly canWriteCatalog: boolean;
  /** Publishing is its own permission, so «Опубликовать» is its own decision. */
  readonly canPublish: boolean;
  readonly canReadInventory: boolean;
  readonly canWriteInventory: boolean;
  readonly roles: readonly string[];
}

export const managementAccess = (session: SessionResponse | null | undefined): ManagementAccess => {
  const canWriteCatalog = hasPermission(session, 'catalog.write');
  const canWriteInventory = hasPermission(session, 'inventory.write');

  return {
    canOpen: canWriteCatalog || canWriteInventory,
    canReadCatalog: hasPermission(session, 'catalog.read'),
    canWriteCatalog,
    /*
     * `catalog.publish` is held by every role that holds `catalog.write`
     * today, but they are separate permissions on the server and one of them
     * can be taken away without the other. Asking for the right one is free.
     */
    canPublish: hasPermission(session, 'catalog.publish'),
    canReadInventory: hasPermission(session, 'inventory.read'),
    canWriteInventory,
    roles: session?.roles ?? [],
  };
};

export type ProductStatus = 'draft' | 'published' | 'hidden' | 'archived';
export type FulfillmentMode = 'automatic' | 'manual' | 'none';
export type InventoryItemType =
  | 'license_key'
  | 'activation_code'
  | 'gift_code'
  | 'activation_url'
  | 'text_payload'
  | 'file'
  | 'manual_task';

/** Mirrors `SCOPE_POLICIES` in `@store/storage`; the scope decides the size
    and type limits the server applies. */
export type UploadScope = 'product' | 'category' | 'broadcast' | 'support';

/**
 * One row of the catalogue list, as the mobile list draws it.
 *
 * `available` is `null` when the product does not count stock at all, and a
 * number when it does — deliberately not `0`, which a row would rightly render
 * as «нет в наличии». Variants carry their own count because a variant owns its
 * own pool: a product can look stocked while the option the customer picked has
 * nothing behind it.
 */
export interface AdminProductRow {
  id: string;
  shortId: string;
  title: string;
  status: ProductStatus;
  basePrice: MoneyJson;
  tracksInventory: boolean;
  fulfillmentMode: FulfillmentMode;
  lowStockThreshold: number;
  categoryId: string | null;
  available: number | null;
  variants: Array<{ id: string; title: string; available: number | null }>;
}

/** A product as every write route answers with it (`productJson`). */
export interface AdminProduct {
  id: string;
  shortId: string;
  slug: string;
  title: string;
  subtitle: string | null;
  status: ProductStatus;
  categoryId: string | null;
  fulfillmentMode: FulfillmentMode;
  tracksInventory: boolean;
  lowStockThreshold: number;
  purchaseLimitPerUser: number | null;
  basePrice: MoneyJson;
}

export interface AdminVariant {
  id: string;
  shortId: string;
  productId: string;
  title: string;
  durationDays: number | null;
  isPublished: boolean;
  price: MoneyJson;
}

export interface AdminMedia {
  id: string;
  url: string;
  kind: string;
  alt: string | null;
  sortOrder: number;
}

/** ASSUMED SHAPE — see the note on {@link adminApi.product}. */
export interface AdminProductDetail {
  product: AdminProduct;
  variants: AdminVariant[];
  media: AdminMedia[];
}

/**
 * What a product write carries.
 *
 * `basePrice` is minor units as a digit string, because that is what the column
 * holds and what the route's `^\d+$` accepts — build it with
 * {@link parseMoneyInput}, never from a float.
 *
 * Every optional field here means "leave this alone" on a patch. That is not a
 * convention, it is the fix for a live defect: the route drops absent keys one
 * by one precisely so that pressing «Опубликовать» cannot also rewrite stock
 * counting. Sending `undefined` and sending the current value are not the same
 * request, so a form must send only what the owner actually changed.
 */
export interface AdminProductInput {
  title: string;
  slug?: string;
  subtitle?: string | null;
  description?: string | null;
  activationInstructions?: string | null;
  categoryId?: string | null;
  basePrice: string;
  baseCurrency?: string;
  status?: ProductStatus;
  fulfillmentMode?: FulfillmentMode;
  tracksInventory?: boolean;
  purchaseLimitPerUser?: number | null;
}

export type AdminProductPatch = Partial<AdminProductInput>;

/** A category row exactly as `/api/admin/categories` returns it — the whole
    row, since a category carries no money column. */
export interface AdminCategory {
  id: string;
  shortId: string;
  parentId: string | null;
  slug: string;
  title: string;
  description: string | null;
  iconEmoji: string | null;
  imageUrl: string | null;
  sortOrder: number;
  isPublished: boolean;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminCategoryInput {
  title: string;
  slug?: string;
  description?: string | null;
  iconEmoji?: string | null;
  /** Must be `https://`; the route refuses plain http, because the Mini App is
      served over TLS and the browser drops the image without saying why. */
  imageUrl?: string | null;
  sortOrder?: number;
  parentId?: string | null;
  isPublished?: boolean;
}

export type AdminCategoryPatch = Partial<AdminCategoryInput>;

/** Stock, masked. The full value needs an explicit reveal with a reason, which
    the Mini App deliberately does not offer. */
export interface AdminInventoryItem {
  id: string;
  productId: string;
  variantId: string | null;
  status: string;
  itemType: InventoryItemType;
  maskedPreview: string | null;
  expiresAt: string | null;
  createdAt: string;
}

export interface AdminInventoryImport {
  productId: string;
  variantId?: string | null;
  itemType: InventoryItemType;
  /** Names the batch in the audit trail, e.g. «Партия от 12 мая». */
  label: string;
  supplier?: string | null;
  /** One key per entry. The payloads are encrypted before they touch the
      database and never appear in the audit log. */
  payloads: string[];
}

/** A dry run: what an import would do, before it does it. */
export interface AdminImportPreview {
  total: number;
  unique: number;
  duplicatesInFile: number;
  alreadyInStock: number;
  invalid: number;
  samples: string[];
}

export interface AdminImportResult {
  batchId: string;
  imported: number;
  skipped: number;
  duplicates: number;
  invalid: number;
}

/**
 * What an upload answers with. Only `url` is relied upon: it is what goes into
 * a category's `imageUrl` or a product's media row.
 */
export interface AdminUploadResult {
  url: string;
  key?: string;
  mimeType?: string;
  size?: number;
}

/*
 * Types the store will accept, mirrored from `SCOPE_POLICIES`.
 *
 * Checked here only so the owner gets a sentence instead of a round trip; the
 * server decides for real, from the file's magic bytes, because a browser's
 * `file.type` is whatever the operating system guessed from the extension.
 * SVG is absent on purpose — it is XML that can carry script.
 */
const IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] as const;
const UPLOAD_MAX_BYTES: Readonly<Record<UploadScope, number>> = {
  product: 5 * 1024 * 1024,
  category: 2 * 1024 * 1024,
  broadcast: 20 * 1024 * 1024,
  support: 10 * 1024 * 1024,
};

/** ASSUMED PATH — the upload route is being written in parallel. Named once so
    a mismatch is a one-line correction rather than a hunt. */
export const ADMIN_UPLOAD_PATH = '/uploads';

/**
 * A second, admin-scoped session, held in memory only.
 *
 * The API derives the session scope from the URL: anything under `/api/admin`
 * is resolved against `scope = 'admin'`, and the Mini App's own session is
 * `scope = 'miniapp'`. So the customer's session token is not merely
 * insufficient here, it does not resolve at all — every management call would
 * come back 403 «Authentication required» no matter who was holding the phone.
 * The management section therefore logs in a second time, with the same signed
 * `initData` and `scope: 'admin'`, and the server refuses that outright unless
 * the account really holds a role.
 *
 * `credentials: 'omit'` on every one of these requests is load-bearing twice
 * over. Outbound: the session cookie wins over the `Authorization` header
 * server-side, so sending the customer's cookie alongside the admin bearer
 * would resolve the customer's token under the admin scope — 403, always.
 * Inbound: both sessions use the same cookie name, so an admin login made with
 * `credentials: 'include'` would overwrite the customer's session cookie and
 * every ordinary screen would start answering 401. A fetch with credentials
 * omitted neither sends nor stores cookies, which keeps the two apart with no
 * server change at all.
 */
interface AdminSession {
  readonly token: string;
  readonly csrfToken: string;
  readonly roles: string[];
  readonly permissions: string[];
}

let adminSession: AdminSession | null = null;

/**
 * The management session's credentials, for the admin transport.
 *
 * They are exposed here and nowhere else because the API resolves a session
 * under the scope the URL implies — `app.ts` reads `/api/admin` as `admin` and
 * everything else as `miniapp` — and `resolveSession` matches that scope
 * exactly. So the customer's token is not merely insufficient for
 * `/api/admin/*`: it is invisible to it. The management screens were handed the
 * customer session and every request came back «Недостаточно прав», which is
 * the server saying it found no session at all.
 *
 * Returns nothing until {@link adminApi.signIn} has succeeded, so a caller
 * cannot accidentally send an administrative request with a customer's
 * credentials.
 */
export const adminAuthHeaders = (): Record<string, string> =>
  adminSession
    ? {
        authorization: `Bearer ${adminSession.token}`,
        'x-csrf-token': adminSession.csrfToken,
      }
    : {};

/**
 * A request that never reached the server.
 *
 * `fetch` rejects with a bare `TypeError` when the network is gone, which a
 * screen cannot tell apart from a bug. Wrapped into the same `ApiError`
 * everything else throws, with `status: 0` and a code of its own, so a form can
 * offer «Повторить» for this and show the server's sentence for a refusal —
 * and, either way, keep everything the owner typed.
 */
const networkError = () =>
  new ApiError('network_error', 'Нет связи с сервером. Проверьте интернет и повторите.', 0);

export const isNetworkError = (error: unknown): boolean =>
  error instanceof ApiError && error.code === 'network_error';

/** A refusal: the request arrived and the server (or this client) said no.
    The form must not be cleared — the owner has to be able to fix the value. */
export const isValidationError = (error: unknown): boolean =>
  error instanceof ApiError && error.code === 'validation_failed';

const adminSignIn = async (): Promise<AdminSession> => {
  let response: Response;
  try {
    response = await fetch(`${API_URL}/api/auth/telegram`, {
      method: 'POST',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify({ initData: getInitData(), scope: 'admin' }),
    });
  } catch {
    throw networkError();
  }

  // A 403 here is the answer to "may I manage this shop", given by the server
  // from roles it read out of the database. It is not an error to paper over.
  const session = await readEnvelope<SessionResponse>(response);
  adminSession = {
    token: session.sessionToken,
    csrfToken: session.csrfToken,
    roles: session.roles,
    permissions: session.permissions,
  };
  return adminSession;
};

async function adminFetch<T>(
  session: AdminSession,
  path: string,
  options: { method?: string; body?: unknown; signal?: AbortSignal },
): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {
    accept: 'application/json',
    authorization: `Bearer ${session.token}`,
  };

  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET') headers['x-csrf-token'] = session.csrfToken;

  let response: Response;
  try {
    response = await fetch(`${API_URL}/api/admin${path}`, {
      method,
      headers,
      credentials: 'omit',
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch {
    throw networkError();
  }

  return readEnvelope<T>(response);
}

async function adminRequest<T>(
  path: string,
  options: { method?: string; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const session = adminSession;
  if (!session) {
    // Nothing is sent. A screen that reaches this has skipped `signIn`, and a
    // customer's build has no other way in.
    throw new ApiError('forbidden', 'Управление недоступно: войдите как администратор', 403);
  }

  try {
    return await adminFetch<T>(session, path, options);
  } catch (error) {
    /*
     * An expired admin session and a missing permission both answer 403, and
     * the message is translated, so they cannot be told apart from the outside.
     * One re-login settles it: if the account still holds the role the retry
     * succeeds, and if it does not, the second 403 is the real answer.
     */
    if (error instanceof ApiError && error.status === 403) {
      adminSession = null;
      return adminFetch<T>(await adminSignIn(), path, options);
    }
    throw error;
  }
}

export const adminApi = {
  /**
   * Establishes the management session. Every other call here needs it.
   *
   * WHEN: right after {@link api.authenticate}, not when the owner taps
   * «Управление». `initData` is minted once, when Telegram launches the Mini
   * App, and the server refuses it after 15 minutes
   * (`TELEGRAM_INITDATA_MAX_AGE_SECONDS`, default 900) — so an owner who has
   * had the shop open for a quarter of an hour cannot log in a second time,
   * even though their customer session is good for hours. Signing in while the
   * proof is fresh buys the full session lifetime.
   * {@link adminApi.signInIfPermitted} is that call, in one line.
   */
  signIn: async (): Promise<{ roles: string[]; permissions: string[] }> => {
    const session = await adminSignIn();
    return { roles: session.roles, permissions: session.permissions };
  },

  /**
   * Signs in only if the customer session already says this account manages the
   * shop, and answers whether it did.
   *
   * The permission check first is what keeps an ordinary customer from ever
   * emitting an administrative request: their session carries no management
   * permission, so nothing is sent and the server is never asked. It stays a
   * presentation decision — an account that *does* hold one still has to get
   * past the server, which re-reads the roles from the database and answers 403
   * on its own terms.
   */
  signInIfPermitted: async (session: SessionResponse | null | undefined): Promise<boolean> => {
    if (!managementAccess(session).canOpen) return false;
    await adminSignIn();
    return true;
  },

  /** Whether a management session is currently held — for a screen deciding
      whether it still needs to call `signIn`, not for deciding what to show. */
  isSignedIn: (): boolean => adminSession !== null,

  /**
   * Drops the management session from memory when the section is left.
   *
   * Local only: `/api/auth/logout` resolves under the miniapp scope, so it
   * cannot see — and therefore cannot revoke — an admin-scoped token. The
   * session expires on the server on its own schedule.
   */
  signOut: (): void => {
    adminSession = null;
  },

  // --- catalogue ----------------------------------------------------------
  products: (signal?: AbortSignal) =>
    adminRequest<{ products: AdminProductRow[] }>('/products', { ...(signal ? { signal } : {}) }),

  /**
   * One product, with its variants and pictures.
   *
   * ASSUMED SHAPE: `GET /api/admin/products/:productId` did not exist when this
   * was written and is being added in parallel; `{ product, variants, media }`
   * is the shape agreed for it. The list row is enough to open an edit form, so
   * a screen can survive this route not landing.
   */
  product: (productId: string, signal?: AbortSignal) =>
    adminRequest<AdminProductDetail>(`/products/${encodeURIComponent(productId)}`, {
      ...(signal ? { signal } : {}),
    }),

  createProduct: (body: AdminProductInput) =>
    adminRequest<{ product: AdminProduct }>('/products', { method: 'POST', body }),

  /** Sends only the keys it is given; see {@link AdminProductInput}. */
  updateProduct: (productId: string, body: AdminProductPatch) =>
    adminRequest<{ product: AdminProduct }>(`/products/${encodeURIComponent(productId)}`, {
      method: 'PATCH',
      body,
    }),

  /**
   * Publish, hide, archive, or send back to draft.
   *
   * A named wrapper around the same PATCH rather than a route of its own, and
   * the body carries exactly one key — which is the whole point. The bug this
   * shape prevents is the one that was live: a publish that also rewrote stock
   * counting, because the fields it did not mention arrived with defaults.
   */
  setProductStatus: (productId: string, status: ProductStatus) =>
    adminRequest<{ product: AdminProduct }>(`/products/${encodeURIComponent(productId)}`, {
      method: 'PATCH',
      body: { status },
    }),

  /**
   * Copies a product, its variants and its media into a new draft.
   *
   * ASSUMED SHAPE: `POST /api/admin/products/:productId/duplicate` is being
   * added in parallel. It has to be a server route — a client-side copy would
   * have to re-create variants and media one call at a time and would leave
   * half a product behind on the first failure.
   */
  duplicateProduct: (productId: string) =>
    adminRequest<{ product: AdminProduct }>(
      `/products/${encodeURIComponent(productId)}/duplicate`,
      { method: 'POST' },
    ),

  /** Adds an option with its own price and its own stock pool. */
  createVariant: (
    productId: string,
    body: { title: string; price: string; currency?: string; durationDays?: number | null },
  ) =>
    adminRequest<{ variant: AdminVariant }>(`/products/${encodeURIComponent(productId)}/variants`, {
      method: 'POST',
      body,
    }),

  categories: (signal?: AbortSignal) =>
    adminRequest<{ categories: AdminCategory[] }>('/categories', {
      ...(signal ? { signal } : {}),
    }),

  createCategory: (body: AdminCategoryInput) =>
    adminRequest<{ category: AdminCategory }>('/categories', { method: 'POST', body }),

  updateCategory: (categoryId: string, body: AdminCategoryPatch) =>
    adminRequest<{ category: AdminCategory }>(`/categories/${encodeURIComponent(categoryId)}`, {
      method: 'PATCH',
      body,
    }),

  // --- stock --------------------------------------------------------------
  inventory: (params: { productId?: string; limit?: number } = {}, signal?: AbortSignal) => {
    const query = new URLSearchParams();
    if (params.productId) query.set('productId', params.productId);
    if (params.limit) query.set('limit', String(params.limit));
    const suffix = query.toString();
    return adminRequest<{ items: AdminInventoryItem[] }>(
      `/inventory${suffix ? `?${suffix}` : ''}`,
      { ...(signal ? { signal } : {}) },
    );
  },

  /** Counts duplicates and rejects without writing anything. Worth showing
      before an import: pasted keys are very often pasted twice. */
  previewImport: (body: AdminInventoryImport) =>
    adminRequest<{ preview: AdminImportPreview }>('/inventory/preview', {
      method: 'POST',
      body,
    }),

  importInventory: (body: AdminInventoryImport) =>
    adminRequest<{ result: AdminImportResult }>('/inventory/import', { method: 'POST', body }),

  /**
   * Sends one picture as a raw body.
   *
   * Not the JSON helper: there is no multipart parser in the API and no new
   * dependency may be added, so the bytes travel as the body with the image's
   * own content type, and everything the server needs beyond them travels in
   * the query string. It cannot be a header — CORS on this API allows exactly
   * four request headers, and a custom one fails the preflight, which looks
   * like the API being down rather than like a rejected upload.
   *
   * Failure is reported in three distinguishable ways, because the screen must
   * keep the form in all three: `network_error` (never arrived, offer a retry),
   * `validation_failed` (the file was refused, show the sentence and let the
   * owner pick another), and anything else (surface the message).
   */
  uploadImage: async (input: {
    file: Blob;
    scope?: UploadScope;
    filename?: string;
    /** Optional: lets a route that stores the picture against a product write
        the `product_media` row itself. */
    productId?: string;
    signal?: AbortSignal;
  }): Promise<AdminUploadResult> => {
    const session = adminSession;
    if (!session) {
      throw new ApiError('forbidden', 'Управление недоступно: войдите как администратор', 403);
    }

    const scope = input.scope ?? 'product';
    const type = input.file.type;
    const limit = UPLOAD_MAX_BYTES[scope];

    if (!(IMAGE_MIME_TYPES as readonly string[]).includes(type)) {
      throw new ApiError(
        'validation_failed',
        'Подойдёт JPEG, PNG, WebP или GIF — этот файл не картинка.',
        0,
      );
    }

    if (input.file.size > limit) {
      throw new ApiError(
        'validation_failed',
        `Файл больше ${Math.round(limit / 1024 / 1024)} МБ. Уменьшите картинку и попробуйте снова.`,
        0,
      );
    }

    const query = new URLSearchParams({ scope });
    const filename = input.filename ?? (input.file as File).name;
    if (filename) query.set('filename', filename);
    if (input.productId) query.set('productId', input.productId);

    let response: Response;
    try {
      response = await fetch(`${API_URL}/api/admin${ADMIN_UPLOAD_PATH}?${query.toString()}`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': type,
          authorization: `Bearer ${session.token}`,
          'x-csrf-token': session.csrfToken,
        },
        credentials: 'omit',
        body: input.file,
        ...(input.signal ? { signal: input.signal } : {}),
      });
    } catch {
      throw networkError();
    }

    /*
     * 413 is Fastify refusing the body before any route ran, so it arrives as
     * the generic envelope and would read to the owner as «что-то пошло не
     * так». It is a size refusal and is reported as one.
     */
    if (response.status === 413) {
      throw new ApiError(
        'validation_failed',
        `Файл больше ${Math.round(limit / 1024 / 1024)} МБ. Уменьшите картинку и попробуйте снова.`,
        413,
      );
    }

    return readEnvelope<AdminUploadResult>(response);
  },
};

/**
 * Formats an amount for display. The value itself stays exact server-side.
 *
 * The space before the currency mark is an ordinary U+0020 and wants to be a
 * U+00A0, so a 34px total can never break as "5000" over "★". It is not one
 * because `money.test.ts` pins all three branches to an ordinary space —
 * `toBe('1990 ★')`, `toBe('49,99 USD')` and a `split(' ')` that asserts the
 * string still contains a breakable space. That file is not this agent's to
 * edit; until it is changed, `white-space: nowrap` on `.price` is what holds
 * the line, and it is asserted in the design tests.
 */
export function formatMoney(money: MoneyJson): string {
  /*
   * `★` and not `⭐`. The emoji is drawn by the platform's colour font, so it
   * arrives at its own size, on its own baseline, in a yellow nobody chose — a
   * cartoon sticker sitting inside a price set in the system face. The text
   * star inherits the size, weight and colour of the number it follows, which
   * is what a currency mark is supposed to do.
   */
  if (money.currency === 'XTR') return `${money.amount} ★`;

  const divisor = 10 ** money.decimals;
  const value = Number(money.amount) / divisor;
  // Dollars as the shop writes them, $12.50; everything else in Russian style.
  const formatted = new Intl.NumberFormat(money.currency === 'USD' ? 'en-US' : 'ru-RU', {
    minimumFractionDigits: value % 1 === 0 ? 0 : money.decimals,
    maximumFractionDigits: money.decimals,
  }).format(value);

  if (money.currency === 'USD') return `$${formatted}`;
  return money.currency === 'RUB' ? `${formatted} ₽` : `${formatted} ${money.currency}`;
}

/* =========================================================================
 * The price the owner types, and the integer the API stores.
 *
 * Every amount on the wire is an integer in the currency's minor unit as a
 * digit string: `'199050'` is 1 990,50 ₽. The owner types «1990,50» on a phone
 * — with a comma, sometimes with a space in the thousands, sometimes with a
 * trailing separator and nothing after it. The conversion between those two
 * lives here, once, in both directions.
 *
 * No float touches either of them. `1990.5 * 100` is 199049.99999999997 in
 * IEEE 754, and `Math.round` hides that until the day it does not; the digits
 * are shifted as text and the only arithmetic is a `BigInt` increment, which is
 * exact at any size.
 * ========================================================================= */

/*
 * Group separators, stripped before anything else is decided.
 *
 * `\s` is doing real work here rather than standing in as a lazy catch-all:
 * the space `Intl.NumberFormat('ru-RU')` sets between thousands is U+00A0, and
 * a price copied off the screen and pasted back into a field arrives carrying
 * it. The two apostrophes are the Swiss convention, which a phone keyboard will
 * happily produce. None of them is a digit and none of them is the decimal mark.
 */
const GROUP_SEPARATORS = /[\s\u2019']/g;

const AMOUNT_SHAPE = /^(\d*)(?:[.,](\d*))?$/;

/**
 * Reads a typed price into minor units, or `null` if it is not a number.
 *
 * ROUNDING: half up on the first discarded digit — «1990,555» becomes
 * `'199056'` for a 2-decimal currency, «0,5» becomes `'1'` for Stars. A price
 * finer than the currency can express has to become one that can, and rounding
 * up by half a kopeck is the direction that never quietly undercharges the
 * shop. It is not silent either: put the result back through
 * {@link formatMoneyInput} and the field shows the owner exactly what will be
 * saved.
 *
 * `null` means "this is not a price" — empty, a stray letter, a minus sign
 * (the API accepts `^\d+$` and a negative price is not a thing), two separators.
 * It is not an error to swallow: the field says «Введите цену» and keeps focus.
 */
export function parseMoneyInput(input: string, decimals: number): string | null {
  if (!Number.isInteger(decimals) || decimals < 0) return null;

  const cleaned = input.replace(GROUP_SEPARATORS, '');
  const match = AMOUNT_SHAPE.exec(cleaned);
  if (!match) return null;

  const whole = match[1] ?? '';
  const fraction = match[2] ?? '';
  // «,» or «.» alone is not a price, and neither is nothing at all.
  if (whole.length + fraction.length === 0) return null;

  const kept = fraction.slice(0, decimals).padEnd(decimals, '0');
  const discarded = fraction.charAt(decimals);
  const roundUp = discarded !== '' && discarded >= '5';

  // `|| '0'` covers «,5» typed into a Stars field: nothing left of the point
  // and nothing kept from the right of it, only the carry.
  return (BigInt(`${whole}${kept}` || '0') + (roundUp ? 1n : 0n)).toString();
}

/**
 * Minor units back into something editable: `'199050'` → `«1990,50»`.
 *
 * Deliberately not {@link formatMoney}: no currency mark and no thousands
 * separator. A field that renders «1 990,50 ₽» while it is being typed in
 * fights the caret, and the space it inserts is the very character
 * {@link parseMoneyInput} then has to strip back out. A whole amount loses its
 * «,00» for the same reason it does on screen — nobody types the zeros.
 *
 * Returns `''` for anything that is not a digit string, so a malformed value
 * from the server renders as an empty field rather than as `NaN`.
 */
export function formatMoneyInput(amount: string, decimals: number): string {
  if (!/^\d+$/.test(amount) || !Number.isInteger(decimals) || decimals < 0) return '';
  if (decimals === 0) return BigInt(amount).toString();

  const padded = amount.padStart(decimals + 1, '0');
  const whole = BigInt(padded.slice(0, -decimals)).toString();
  const fraction = padded.slice(-decimals);

  return /^0*$/.test(fraction) ? whole : `${whole},${fraction}`;
}

/** Seeds an edit field from an amount the server sent, which already carries
    its own `decimals` — the reliable way to get them right. */
export const moneyToInput = (money: MoneyJson): string =>
  formatMoneyInput(money.amount, money.decimals);

/**
 * Minor units per currency, for a NEW price, where there is no `MoneyJson` to
 * read `decimals` off yet.
 *
 * A copy of what `DEFAULT_CURRENCIES` in `@store/shared` says about the
 * currencies a shop can be priced in — not the crypto assets, which are
 * payment currencies and are never typed into this form. Getting one of these
 * wrong is a price off by a factor of a hundred, so the fallback is the fiat
 * convention rather than something clever.
 */
const CURRENCY_DECIMALS: Readonly<Record<string, number>> = {
  RUB: 2,
  USD: 2,
  EUR: 2,
  KZT: 2,
  UAH: 2,
  XTR: 0,
};

export const decimalsForCurrency = (currency: string): number =>
  CURRENCY_DECIMALS[currency.toUpperCase()] ?? 2;
